import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getInstitutionRequests, denyInstitutionRequest, getUserInfo } from '../../utils/remote';
import { useAuth } from '../../utils/Auth/AuthProvider';
import { useState, useMemo, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Search, Clock, Check, X, GraduationCap, TriangleAlert } from 'lucide-react';
import {
  PageHeader, PanelCard, MetricCard, PillTabs, Badge, Button, EmptyState, Spinner, ErrorPanel, PagedFooter,
  Checkbox, Initials, ConfirmDialog, TextLinkButton,
} from '../../school_panel/components/ui';
import TopBarActions from '../../components/Header/TopBarActions';
import { usePagination } from '../../school_panel/lib/usePagination';
import { apiErrorText } from '../../school_panel/lib/apiError';
import { SchoolApi } from '../../school_panel/api/client';
import { useApproveJoinRequest } from '../../school_panel/api/hooks';
import ApproveRequestDialog, { type JoinRequestRow } from './components/ApproveRequestDialog';
import { LINK_BTN } from '../../school_panel/components/ui/linkButton';
import { plural } from '../../school_panel/lib/plural';
import { formatRoll } from '../../school_panel/lib/roll';
import { timeOf } from '../../school_panel/lib/dates';

type Status = 'pending' | 'approved' | 'denied';
// The backend status stays `denied`; the screen says "Rejected" (artboard copy).
const STATUS_LABEL: Record<Status, string> = { pending: 'Pending', approved: 'Approved', denied: 'Rejected' };
const STATUS_TONE: Record<Status, 'warning' | 'success' | 'danger'> = { pending: 'warning', approved: 'success', denied: 'danger' };

const FIELD_WRAP = 'flex min-h-[44px] min-w-[150px] flex-1 items-center gap-2 rounded-[12px] border border-line-strong bg-surface px-3.5 focus-within:ring-2 focus-within:ring-accent';
const FIELD_INPUT = 'min-w-0 flex-1 border-0 bg-transparent py-2 text-sm text-ink outline-none placeholder:text-muted';

/** "oldest waiting 2 days": whole days since the request, or "today". */
const waitingFor = (iso: string): string => {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (!Number.isFinite(days) || days < 1) return 'today';
  return `${days} day${days === 1 ? '' : 's'}`;
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Design "when": `Today, 8:42 AM`, `Yesterday, 6:03 PM`, `10 Oct, 2:20 PM` (with the year when it is not this one). */
export const whenLabel = (iso: string, now: Date = new Date()): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const time = timeOf(d);
  const midnight = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const dayDiff = Math.round((midnight(now) - midnight(d)) / 86400000);
  if (dayDiff === 0) return `Today, ${time}`;
  if (dayDiff === 1) return `Yesterday, ${time}`;
  const year = d.getFullYear() === now.getFullYear() ? '' : ` ${d.getFullYear()}`;
  return `${d.getDate()} ${MONTHS[d.getMonth()]}${year}, ${time}`;
};


type Bulk = { action: 'approve' | 'reject'; ids: number[]; all: boolean } | null;

const StudentRequests = () => {
  const { token } = useAuth() as any;
  const qc = useQueryClient();
  const approveMut = useApproveJoinRequest();
  const [placing, setPlacing] = useState<{ id: number; mode: 'approve' | 'reject' } | null>(null);
  const [activeTab, setActiveTab] = useState<Status>('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulk, setBulk] = useState<Bulk>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ text: string; tone: 'ok' | 'warn' } | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['institutionRequests'],
    queryFn: () => getInstitutionRequests(token),
    enabled: !!token,
  });
  const schoolNameQ = useQuery({
    queryKey: ['school.name'],
    queryFn: async () => {
      const info = await getUserInfo(token || undefined);
      return (info?.institution_name as string) ?? '';
    },
    staleTime: Infinity,
    enabled: !!token,
  });
  const gradesQ = useQuery({
    queryKey: ['analytics.grades'],
    queryFn: () => SchoolApi.analyticsGrades(),
  });
  const sectionName = useMemo(() => {
    const m = new Map<number, string>();
    (gradesQ.data?.grades ?? []).forEach((g: any) => (g.sections ?? []).forEach((s: any) => m.set(s.id, s.name)));
    return m;
  }, [gradesQ.data]);

  const requests = useMemo(() => data?.requests || [], [data]);

  const filteredRequests = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return requests.filter((r: any) => {
      const matchesSearch =
        r.full_name?.toLowerCase().includes(term) ||
        r.email?.toLowerCase().includes(term) ||
        r.grade_name?.toLowerCase().includes(term);
      return r.status === activeTab && matchesSearch;
    });
  }, [requests, activeTab, searchTerm]);

  const stats = useMemo(() => ({
    pending: requests.filter((r: any) => r.status === 'pending').length,
    approved: requests.filter((r: any) => r.status === 'approved').length,
    denied: requests.filter((r: any) => r.status === 'denied').length,
  }), [requests]);

  const pendingRows = useMemo(() => requests.filter((r: any) => r.status === 'pending'), [requests]);
  const oldestPending = useMemo(() => {
    const times = pendingRows
      .filter((r: any) => r.created)
      .map((r: any) => new Date(r.created).getTime())
      .filter((t: number) => Number.isFinite(t));
    return times.length ? new Date(Math.min(...times)).toISOString() : null;
  }, [pendingRows]);

  const schoolName = schoolNameQ.data;
  const subtitle = stats.pending > 0
    ? `${stats.pending} ${stats.pending === 1 ? 'student is' : 'students are'} waiting to join ${schoolName || 'your school'}. Approve to place them in a class.`
    : 'No one is waiting. Every join request has been handled.';

  const openPlace = (mode: 'approve' | 'reject', id: number) => { setActionError(null); returnFocusTo.current = `${id}:${mode}`; setPlacing({ id, mode }); };
  const closePlace = () => { if (!busy) { setPlacing(null); setActionError(null); } };

  const rejectOne = async (r: any) => {
    if (busy) return;
    setBusy(true);
    setActionError(null);
    try {
      await denyInstitutionRequest(token, r.id);
      qc.invalidateQueries({ queryKey: ['institutionRequests'] });
      setPlacing(null);
      setNotice({ text: `Rejected the request from ${r.full_name || 'this student'}. They have been notified.`, tone: 'ok' });
    } catch (e) {
      setActionError(apiErrorText(e, 'Could not reject this request. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  // Bulk actions loop the existing approve / deny endpoints, one request at a time.
  const runBulk = async () => {
    if (!bulk || busy) return;
    setBusy(true);
    const rows = pendingRows.filter((r: any) => bulk.ids.includes(r.id));
    let done = 0;
    const failed: string[] = [];
    for (const r of rows) {
      try {
        if (bulk.action === 'approve') {
          const roll = r.roll_number ?? r.suggested_roll;
          await approveMut.mutateAsync({
            id: r.id,
            body: {
              ...(r.grade_id != null ? { grade_id: r.grade_id } : {}),
              section_id: r.section_id ?? null,
              roll_number: roll ?? null,
            },
          });
        } else {
          await denyInstitutionRequest(token, r.id);
        }
        done += 1;
      } catch {
        failed.push(r.full_name || 'a student');
      }
    }
    qc.invalidateQueries({ queryKey: ['institutionRequests'] });
    setSelected(new Set());
    setBulk(null);
    setBusy(false);
    const verb = bulk.action === 'approve' ? 'Approved' : 'Rejected';
    const tail = bulk.action === 'approve' ? ' to the class each student asked for.' : '. The students have been notified.';
    setNotice(failed.length
      ? { text: `${verb} ${plural(done, 'request')}. Could not ${bulk.action} ${failed.join(', ')}; ${bulk.action === 'approve' ? 'a roll number may be taken, so approve them one at a time' : 'try again'}.`, tone: 'warn' }
      : { text: `${verb} ${plural(done, 'request')}${tail}`, tone: 'ok' });
  };

  const tabItems = (['pending', 'approved', 'denied'] as const).map((k) => ({
    key: k,
    label: STATUS_LABEL[k],
    count: stats[k],
    dot: STATUS_TONE[k],
  }));
  const pg = usePagination(filteredRequests, 10, `${activeTab}|${searchTerm}`);
  // Selection belongs to the view it was made in (as on the Students page): a page, tab or search change clears it.
  useEffect(() => { setSelected(new Set()); }, [pg.page, activeTab, searchTerm]);
  // Inline panels take focus when they open; closing hands it back to the row's action button.
  const rejectPanelRef = useRef<HTMLDivElement>(null);
  const returnFocusTo = useRef<string | null>(null);
  useEffect(() => {
    if (placing?.mode === 'reject') rejectPanelRef.current?.focus();
    if (!placing && returnFocusTo.current != null) {
      document.querySelector<HTMLElement>(`[data-req-action="${returnFocusTo.current}"]`)?.focus();
      returnFocusTo.current = null;
    }
  }, [placing]);

  const pendingVisible = activeTab === 'pending' ? filteredRequests : [];
  const selectedIds = Array.from(selected).filter((id) => pendingRows.some((r: any) => r.id === id));
  const allOn = pendingVisible.length > 0 && pendingVisible.every((r: any) => selected.has(r.id));
  const toggleOne = (id: number) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const emptyCopy: Record<Status, [string, string]> = {
    pending: ['You are all caught up', 'No students are waiting to join. New requests appear here when students enter your school code in the app.'],
    approved: ['No approved requests yet', 'Students you approve show up here with their class and roll number.'],
    denied: ['No rejected requests', 'Requests you reject show up here.'],
  };

  const lineFor = (r: any, status: Status): string | null => {
    const sec = r.section_id != null ? sectionName.get(r.section_id) : undefined;
    if (status === 'approved') {
      return `Placed in ${r.grade_name || 'no grade'}${sec ? ` · ${sec}` : ''}${r.roll_number ? ` · roll ${formatRoll(r.roll_number)}` : ''}`;
    }
    if (status === 'pending') {
      return `Asked for ${r.grade_name || 'no grade set'}${sec ? ` · Section ${sec}` : ' · no section chosen'}`;
    }
    return null;
  };

  const bulkTitle = bulk
    ? bulk.all ? `Approve all ${bulk.ids.length} pending?` : `${bulk.action === 'approve' ? 'Approve' : 'Reject'} ${plural(bulk.ids.length, 'request')}?`
    : '';

  return (
    <>
      <PageHeader
        eyebrow="Students · join requests"
        title="Student requests"
        subtitle={subtitle}
        actions={<Link to="/students" className={LINK_BTN}><GraduationCap size={18} strokeWidth={1.8} aria-hidden="true" />All students</Link>}
      />
      {stats.pending > 0 && (
        <TopBarActions>
          <Button variant="primary" className="order-last" icon={<Check size={18} strokeWidth={1.8} aria-hidden="true" />}
            onClick={() => setBulk({ action: 'approve', ids: pendingRows.map((r: any) => r.id), all: true })}>
            Approve all {stats.pending} pending
          </Button>
        </TopBarActions>
      )}

      <div className="mb-[18px] grid grid-cols-1 gap-[18px] sm:grid-cols-3">
        <MetricCard tone="tint" icon={<Clock size={22} strokeWidth={1.8} />} label="Pending" value={stats.pending} sub={oldestPending ? `oldest waiting ${waitingFor(oldestPending)}` : 'nothing waiting'} />
        <MetricCard tone="success" icon={<Check size={22} strokeWidth={1.8} />} label="Approved" value={stats.approved} sub="placed in a section" />
        <MetricCard tone="danger" icon={<X size={22} strokeWidth={1.8} />} label="Rejected" value={stats.denied} sub="students are notified" />
      </div>

      <PanelCard className="!p-0" aria-label="Join requests">
        <div className="flex flex-wrap items-center justify-between gap-3.5 px-[22px] py-[18px] max-sm:px-4">
          <PillTabs tabs badge label="Request status" value={activeTab} onChange={(k) => { setActiveTab(k); setPlacing(null); }} options={tabItems} />
          <div className={`${FIELD_WRAP} max-w-[340px] basis-[260px] max-sm:max-w-none`}>
            <Search size={18} strokeWidth={1.8} aria-hidden="true" className="flex-none text-muted" />
            <input
              id="requests-search"
              type="search"
              aria-label="Search"
              placeholder="Name, email or grade…"
              className={FIELD_INPUT}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        {activeTab === 'pending' && filteredRequests.length > 0 && !isLoading && (
          <div className="flex flex-wrap items-center justify-between gap-2 px-[22px] pb-3 text-[13px] text-muted max-sm:px-4">
            <label className="inline-flex items-center gap-2.5">
              <Checkbox aria-label="Select all pending requests" checked={allOn}
                onChange={() => setSelected(allOn ? new Set() : new Set(pendingVisible.map((r: any) => r.id)))} />
              Select all
            </label>
            <span>Approving places the student in a grade, section and roll number.</span>
          </div>
        )}

        {notice && (
          <div className="px-[22px] pb-3.5 max-sm:px-4">
            <div role="status" className={`flex flex-wrap items-center gap-2.5 rounded-[14px] py-2.5 pl-3.5 pr-2.5 font-medium ${notice.tone === 'ok' ? 'bg-success-bg text-success' : 'bg-warning-bg text-warning-ink'}`}>
              {notice.tone === 'ok'
                ? <Check size={18} strokeWidth={1.8} aria-hidden="true" className="flex-none" />
                : <TriangleAlert size={18} strokeWidth={1.8} aria-hidden="true" className="flex-none" />}
              <span className="min-w-0 flex-[1_1_200px]">{notice.text}</span>
              <TextLinkButton onClick={() => setNotice(null)}>Dismiss</TextLinkButton>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center py-10"><Spinner size="md" /></div>
        ) : isError ? (
          <ErrorPanel title="Couldn't load requests" body="There was an error talking to the server." onRetry={() => qc.invalidateQueries({ queryKey: ['institutionRequests'] })} />
        ) : filteredRequests.length === 0 ? (
          <div className="border-t border-hairline">
            <EmptyState
              size="lg"
              icon={<Check size={22} strokeWidth={1.8} />}
              title={searchTerm ? `Nothing matches “${searchTerm}”` : emptyCopy[activeTab][0]}
              body={searchTerm ? `No ${STATUS_LABEL[activeTab].toLowerCase()} requests match. Clear the search to see all of them.` : emptyCopy[activeTab][1]}
              action={searchTerm ? <Button variant="ghost" onClick={() => setSearchTerm('')}>Clear search</Button> : undefined}
            />
          </div>
        ) : (
          <ul>
            {pg.pageItems.map((r: any) => {
              const status: Status = (r.status in STATUS_LABEL ? r.status : 'pending') as Status;
              const name = r.full_name || 'Unnamed student';
              const isPending = status === 'pending';
              const on = selected.has(r.id);
              const line = lineFor(r, status);
              const open = placing && placing.id === r.id ? placing.mode : null;
              return (
                <li key={r.id} className={`flex flex-wrap items-center gap-3.5 border-t border-hairline px-4 py-4 hover:bg-surface-2 sm:px-[22px] ${on ? 'bg-row-sel' : ''}`}>
                  {isPending && (
                    <Checkbox aria-label={`Select ${name}`} checked={on} onChange={() => toggleOne(r.id)} />
                  )}
                  <div className="flex min-w-0 flex-[1_1_260px] items-center gap-3">
                    <Initials name={name} />
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h4 className="break-words font-semibold text-ink">{name}</h4>
                        <Badge tone={STATUS_TONE[status]} size="sm">{STATUS_LABEL[status]}</Badge>
                      </div>
                      <p className="break-all text-[13px] text-muted">{r.email || 'No email'}</p>
                      {line && <p className="mt-0.5 text-[13px] text-ink">{line}</p>}
                    </div>
                  </div>
                  <p className="flex-[0_1_170px] text-[13px] text-muted max-sm:basis-full max-sm:pl-12">
                    <b className="block font-medium text-ink">Requested</b>
                    {whenLabel(r.created)}
                  </p>
                  {isPending && !open && (
                    <div className="ml-auto flex flex-wrap gap-2 max-sm:ml-12">
                      <Button variant="danger-outline" size="sm" data-req-action={`${r.id}:reject`} onClick={() => openPlace('reject', r.id)}>Reject</Button>
                      <Button variant="secondary" size="sm" icon={<Check size={16} strokeWidth={2} aria-hidden="true" />} data-req-action={`${r.id}:approve`} onClick={() => openPlace('approve', r.id)}>Approve</Button>
                    </div>
                  )}
                  {isPending && open === 'approve' && (
                    <ApproveRequestDialog request={r as JoinRequestRow} onClose={closePlace}
                      onApproved={(text) => setNotice({ text, tone: 'ok' })} />
                  )}
                  {isPending && open === 'reject' && (
                    <div ref={rejectPanelRef} tabIndex={-1} role="alertdialog" aria-label={`Reject request from ${name}`}
                      className="box-border flex basis-full flex-wrap items-end gap-3 rounded-[16px] border border-danger-line bg-danger-bg/40 p-4 outline-none">
                      <h3 className="basis-full font-display text-[15px] font-bold text-ink">Reject the request from {name}?</h3>
                      <p className="-mt-1.5 basis-full text-[13px] text-muted">They will be notified and can ask again later.</p>
                      {actionError && <p role="alert" className="basis-full text-sm text-danger-ink">{actionError}</p>}
                      <span className="flex-1" />
                      <Button variant="ghost" size="sm" onClick={closePlace} disabled={busy}>Cancel</Button>
                      <Button variant="danger" size="sm" loading={busy} onClick={() => rejectOne(r)}>Reject request</Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {!isLoading && !isError && filteredRequests.length > 0 && (
          <PagedFooter divider="card" className="max-sm:!px-4" from={pg.from} to={pg.to} total={pg.total} page={pg.page} pageCount={pg.pageCount} onPage={pg.setPage} label="Requests pagination" />
        )}
      </PanelCard>

      {activeTab === 'pending' && selectedIds.length > 0 && (
        <div role="region" aria-label="Bulk actions"
          className="sticky bottom-4 z-[5] mt-4 flex flex-wrap items-center gap-2.5 rounded-[18px] bg-navy py-2.5 pl-[18px] pr-3 text-white shadow-actbar">
          <span className="font-semibold">{selectedIds.length} selected</span>
          <span className="text-[13px] text-on-navy">Approves to the grade and section each student asked for, with the suggested roll.</span>
          <span className="flex-1" />
          <Button variant="on-navy" onClick={() => setBulk({ action: 'reject', ids: selectedIds, all: false })}>Reject {selectedIds.length}</Button>
          <Button variant="primary" icon={<Check size={18} strokeWidth={1.8} aria-hidden="true" />}
            onClick={() => setBulk({ action: 'approve', ids: selectedIds, all: false })}>Approve {selectedIds.length}</Button>
          <Button variant="ghost-on-navy" onClick={() => setSelected(new Set())}>Clear</Button>
        </div>
      )}

      <ConfirmDialog
        isOpen={bulk !== null}
        title={bulkTitle}
        body={bulk?.action === 'approve'
          ? 'Each student is placed in the grade and section they asked for, with the suggested roll number. This cannot be undone from here.'
          : 'The students are notified and can ask again later.'}
        confirmLabel={bulk?.action === 'approve' ? 'Approve' : 'Reject'}
        danger={bulk?.action === 'reject'}
        busy={busy}
        onConfirm={runBulk}
        onCancel={() => { if (!busy) setBulk(null); }}
      />
    </>
  );
};

export default StudentRequests;
